<template>
  <v-dialog v-model="showDialog" max-width="500px" persistent @update:model-value="onDialogClose">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>
          <v-icon start>mdi-phone-plus</v-icon>
          Adauga numarul tau
        </span>
        <v-btn icon variant="text" @click="closeDialog" :disabled="submitting">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Step 1: Enter Phone Number and Select Network -->
        <div v-if="currentStep === 1">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Introdu numarul de telefon si selecteaza reteaua din care face parte pentru a-l inregistra in platforma.
          </v-alert>

          <v-text-field
            v-model="phoneNumber"
            label="Numar de telefon"
            placeholder="07xxxxxxxx"
            prepend-inner-icon="mdi-phone"
            :rules="phoneRules"
            :disabled="submitting"
            variant="outlined"
            class="mb-3"
            @keyup.enter="proceedToVerification"
          ></v-text-field>

          <v-select
            v-model="selectedMvnoId"
            :items="mvnosList"
            item-title="name"
            item-value="id"
            label="Reteaua mobila"
            prepend-inner-icon="mdi-signal"
            :loading="loadingMvnos"
            :disabled="loadingMvnos || submitting"
            variant="outlined"
            class="mb-3"
          >
            <template v-slot:item="{ item, props }">
              <v-list-item v-bind="props">
                <template v-slot:prepend>
                  <v-icon :color="item.raw.own_network ? 'success' : 'grey'">
                    {{ item.raw.own_network ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                </template>
                <template v-slot:subtitle v-if="item.raw.own_network">
                  Retea proprie
                </template>
              </v-list-item>
            </template>
          </v-select>

          <v-alert v-if="errorMessage" type="error" density="compact" class="mb-3">
            {{ errorMessage }}
          </v-alert>
        </div>

        <!-- Step 2: SMS Verification -->
        <div v-if="currentStep === 2">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            Am trimis un cod de verificare prin SMS la numarul <strong>{{ phoneNumber }}</strong>.
            Introdu codul primit pentru a confirma ca numarul iti apartine.
          </v-alert>

          <div class="text-center mb-4">
            <v-otp-input
              v-model="verificationCode"
              length="6"
              type="number"
              :disabled="submitting"
              @finish="verifyCode"
            ></v-otp-input>
          </div>

          <!-- Countdown timer for code expiry -->
          <div class="text-center text-grey mb-3" v-if="codeExpiresIn > 0">
            Codul expira in {{ formatTime(codeExpiresIn) }}
          </div>

          <!-- Resend code with cooldown -->
          <div class="text-center mb-3">
            <v-btn
              variant="text"
              size="small"
              @click="resendCode"
              :disabled="resendCooldown > 0 || submitting"
              :loading="sendingCode"
            >
              <v-icon start size="small">mdi-refresh</v-icon>
              {{ resendCooldown > 0 ? `Retrimite (${resendCooldown}s)` : 'Retrimite codul' }}
            </v-btn>
          </div>

          <!-- Attempts remaining -->
          <div class="text-center text-caption text-grey mb-3" v-if="attemptsRemaining !== null">
            Incercari ramase: {{ attemptsRemaining }}
          </div>

          <v-alert v-if="errorMessage" type="error" density="compact" class="mb-3">
            {{ errorMessage }}
          </v-alert>

          <v-btn
            variant="text"
            size="small"
            @click="goBackToStep1"
            :disabled="submitting"
            class="mt-2"
          >
            <v-icon start size="small">mdi-arrow-left</v-icon>
            Schimba numarul
          </v-btn>
        </div>

        <!-- Step 3: Success -->
        <div v-if="currentStep === 3" class="text-center py-4">
          <v-icon color="success" size="80" class="mb-4">mdi-check-circle</v-icon>
          <div class="text-h6 mb-2">Numar verificat cu succes!</div>
          <div class="text-body-2 text-grey mb-4">
            Numarul <strong>{{ phoneNumber }}</strong> a fost adaugat in contul tau.
          </div>
          <v-chip color="success" variant="tonal">
            <v-icon start>mdi-signal</v-icon>
            {{ selectedMvnoName }}
          </v-chip>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>

        <!-- Step 1 Actions -->
        <template v-if="currentStep === 1">
          <v-btn variant="outlined" @click="closeDialog" :disabled="submitting">Anuleaza</v-btn>
          <v-btn
            color="primary"
            @click="proceedToVerification"
            :loading="submitting"
            :disabled="!isStep1Valid"
          >
            Trimite SMS
            <v-icon end>mdi-send</v-icon>
          </v-btn>
        </template>

        <!-- Step 2 Actions -->
        <template v-if="currentStep === 2">
          <v-btn variant="outlined" @click="closeDialog" :disabled="submitting">Anuleaza</v-btn>
          <v-btn
            color="primary"
            @click="verifyCode"
            :loading="submitting"
            :disabled="verificationCode.length !== 6"
          >
            Verifica
            <v-icon end>mdi-check</v-icon>
          </v-btn>
        </template>

        <!-- Step 3 Actions -->
        <template v-if="currentStep === 3">
          <v-btn color="primary" @click="closeDialog">
            Inchide
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import {
  getMvnosList,
  addUserMsisdn,
  sendMsisdnVerificationCode,
  verifyMsisdnCode
} from "@/composables/useUserMsisdnsAPI";
import store from "@/store";

export default defineComponent({
  name: 'AddUserMsisdnDialog',
  emits: ['close', 'msisdnAdded'],
  data() {
    return {
      showDialog: true,
      currentStep: 1,
      submitting: false,
      sendingCode: false,
      loadingMvnos: false,

      // Form data
      phoneNumber: '',
      selectedMvnoId: null,
      verificationCode: '',

      // MVNOs list
      mvnosList: [],

      // Verification state
      msisdnId: null,
      codeExpiresIn: 0,
      resendCooldown: 0,
      attemptsRemaining: null,

      // Timers
      expiryTimer: null,
      cooldownTimer: null,

      // Messages
      errorMessage: null,

      // Validation rules
      phoneRules: [
        v => !!v || 'Numarul de telefon este obligatoriu',
        v => /^(07[0-9]{8}|\+407[0-9]{8})$/.test(v) || 'Format invalid (ex: 07xxxxxxxx)'
      ]
    }
  },
  computed: {
    isStep1Valid() {
      const phoneRegex = /^(07[0-9]{8}|\+407[0-9]{8})$/
      return phoneRegex.test(this.phoneNumber) && this.selectedMvnoId !== null
    },
    selectedMvnoName() {
      const mvno = this.mvnosList.find(m => m.id === this.selectedMvnoId)
      return mvno ? mvno.name : ''
    }
  },
  methods: {
    async loadMvnos() {
      this.loadingMvnos = true
      try {
        const token = store.getters.getToken
        const response = await getMvnosList(token)
        if (!response.error) {
          this.mvnosList = response.mvnos || []
          log("Loaded MVNOs:")
          log(this.mvnosList)
        } else {
          this.errorMessage = response.message || "Eroare la incarcarea retelelor"
        }
      } catch (err) {
        log("Error loading MVNOs:")
        log(err)
        this.errorMessage = "Eroare la incarcarea retelelor"
      } finally {
        this.loadingMvnos = false
      }
    },

    async proceedToVerification() {
      if (!this.isStep1Valid) return

      this.submitting = true
      this.errorMessage = null

      try {
        const token = store.getters.getToken

        // First, add the MSISDN
        const addResponse = await addUserMsisdn(token, this.phoneNumber, this.selectedMvnoId)

        if (addResponse.error) {
          this.errorMessage = addResponse.message || "Eroare la adaugarea numarului"
          this.submitting = false
          return
        }

        this.msisdnId = addResponse.msisdn?.id || addResponse.msisdn_id

        // Then send verification code
        const sendResponse = await sendMsisdnVerificationCode(token, this.msisdnId, 'sms')

        if (sendResponse.error) {
          this.errorMessage = sendResponse.message || "Eroare la trimiterea codului"
          this.submitting = false
          return
        }

        // Start timers
        this.codeExpiresIn = sendResponse.expires_in || 600 // 10 minutes default
        this.resendCooldown = sendResponse.cooldown || 60 // 60 seconds default
        this.attemptsRemaining = sendResponse.attempts_remaining

        this.startExpiryTimer()
        this.startCooldownTimer()

        // Move to step 2
        this.currentStep = 2
      } catch (err) {
        log("Error in proceedToVerification:")
        log(err)
        this.errorMessage = "Eroare la procesare"
      } finally {
        this.submitting = false
      }
    },

    async resendCode() {
      if (this.resendCooldown > 0) return

      this.sendingCode = true
      this.errorMessage = null

      try {
        const token = store.getters.getToken
        const response = await sendMsisdnVerificationCode(token, this.msisdnId, 'sms')

        if (response.error) {
          this.errorMessage = response.message || "Eroare la retrimiterea codului"
        } else {
          // Reset timers
          this.codeExpiresIn = response.expires_in || 600
          this.resendCooldown = response.cooldown || 60
          this.attemptsRemaining = response.attempts_remaining
          this.verificationCode = ''

          this.startExpiryTimer()
          this.startCooldownTimer()
        }
      } catch (err) {
        log("Error in resendCode:")
        log(err)
        this.errorMessage = "Eroare la retrimiterea codului"
      } finally {
        this.sendingCode = false
      }
    },

    async verifyCode() {
      if (this.verificationCode.length !== 6) return

      this.submitting = true
      this.errorMessage = null

      try {
        const token = store.getters.getToken
        const response = await verifyMsisdnCode(token, this.msisdnId, this.verificationCode)

        if (response.error) {
          this.errorMessage = response.message || "Cod invalid"
          this.attemptsRemaining = response.attempts_remaining
          this.verificationCode = ''
        } else {
          // Success!
          this.stopTimers()
          this.currentStep = 3
          this.$emit('msisdnAdded', {
            msisdn_id: this.msisdnId,
            msisdn: this.phoneNumber,
            mvno_id: this.selectedMvnoId,
            mvno_name: this.selectedMvnoName
          })
        }
      } catch (err) {
        log("Error in verifyCode:")
        log(err)
        this.errorMessage = "Eroare la verificare"
      } finally {
        this.submitting = false
      }
    },

    goBackToStep1() {
      this.stopTimers()
      this.currentStep = 1
      this.verificationCode = ''
      this.errorMessage = null
    },

    startExpiryTimer() {
      this.stopExpiryTimer()
      this.expiryTimer = setInterval(() => {
        if (this.codeExpiresIn > 0) {
          this.codeExpiresIn--
        } else {
          this.stopExpiryTimer()
          this.errorMessage = "Codul a expirat. Te rugam sa retrimiti un cod nou."
        }
      }, 1000)
    },

    startCooldownTimer() {
      this.stopCooldownTimer()
      this.cooldownTimer = setInterval(() => {
        if (this.resendCooldown > 0) {
          this.resendCooldown--
        } else {
          this.stopCooldownTimer()
        }
      }, 1000)
    },

    stopExpiryTimer() {
      if (this.expiryTimer) {
        clearInterval(this.expiryTimer)
        this.expiryTimer = null
      }
    },

    stopCooldownTimer() {
      if (this.cooldownTimer) {
        clearInterval(this.cooldownTimer)
        this.cooldownTimer = null
      }
    },

    stopTimers() {
      this.stopExpiryTimer()
      this.stopCooldownTimer()
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },

    closeDialog() {
      this.stopTimers()
      this.showDialog = false
      this.$emit('close')
    },

    onDialogClose(value) {
      if (!value) {
        this.stopTimers()
        this.$emit('close')
      }
    }
  },

  mounted() {
    this.loadMvnos()
  },

  beforeUnmount() {
    this.stopTimers()
  }
})
</script>

<style scoped>
.v-otp-input {
  max-width: 300px;
  margin: 0 auto;
}
</style>
